import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/bits";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Layers, Plus, MonitorSmartphone, RefreshCw, RotateCw, Power } from "lucide-react";

const GROUP_COMMANDS = [
  { type: "SYNC", label: "Sync", icon: RefreshCw },
  { type: "RESTART", label: "Neustart", icon: RotateCw },
  { type: "REBOOT", label: "Reboot", icon: Power },
];

export default function Groups() {
  const [groups, setGroups] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [devices, setDevices] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPlaylist, setNewPlaylist] = useState("");
  const [devicesFor, setDevicesFor] = useState(null);
  const [selected, setSelected] = useState([]);
  const [deleteArmed, setDeleteArmed] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/groups").then((r) => setGroups(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/playlists").then((r) => setPlaylists(r.data)).catch(() => {});
    api.get("/devices").then((r) => setDevices(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => api.get("/groups").then((r) => setGroups(r.data)).catch(() => {}), 8000);
    return () => clearInterval(t);
  }, [load]);

  async function createGroup() {
    if (!newName.trim() || busy) return;
    setBusy(true);
    try {
      await api.post("/groups", { name: newName.trim(), playlist_id: newPlaylist || null });
      toast.success(`Gruppe „${newName.trim()}" angelegt`);
      setCreateOpen(false);
      setNewName("");
      setNewPlaylist("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function setPlaylist(g, pid) {
    try {
      await api.patch(`/groups/${g.id}`, { playlist_id: pid || "" });
      const pl = playlists.find((p) => p.id === pid);
      toast.success(pid ? `„${pl?.name}" läuft jetzt auf allen Geräten der Gruppe` : "Gruppen-Playlist entfernt");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function saveDevices() {
    if (!devicesFor || busy) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/groups/${devicesFor.id}/devices`, { device_ids: selected });
      toast.success(`${data.members} Gerät${data.members === 1 ? "" : "e"} in „${devicesFor.name}"`);
      setDevicesFor(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function groupCommand(g, type) {
    try {
      const { data } = await api.post("/commands/bulk", { type, group_id: g.id });
      const label = GROUP_COMMANDS.find((c) => c.type === type)?.label || type;
      toast.success(`${label} an ${data.enqueued} Gerät${data.enqueued === 1 ? "" : "e"} der Gruppe „${g.name}" gesendet`);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function deleteGroup(g) {    try {
      await api.delete(`/groups/${g.id}`);
      toast.success(`Gruppe „${g.name}" gelöscht — Geräte bleiben erhalten`);
      setDeleteArmed(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  function openDevices(g) {
    setSelected(devices.filter((d) => d.group_id === g.id).map((d) => d.id));
    setDevicesFor(g);
  }

  const groupNames = Object.fromEntries((groups || []).map((g) => [g.id, g.name]));
  const pairedDevices = devices.filter((d) => d.status !== "SETUP");

  return (
    <div data-testid="groups-page">
      <PageHeader title="Gruppen" sub="Mehrere Displays gemeinsam bespielen — eine Playlist für die ganze Gruppe">
        <button onClick={() => setCreateOpen(true)} className="btn-primary" data-testid="group-create-open">
          <Plus size={15} /> Neue Gruppe
        </button>
      </PageHeader>

      <div className="panel px-4 py-3 mb-6" data-testid="groups-precedence-hint">
        <p className="text-[11px] text-slate-300 leading-relaxed">
          <span className="text-cyan-400 font-mono">Regel:</span> Ein Zeitplan schlägt die Geräte-Playlist,
          die Geräte-Playlist schlägt die Gruppen-Playlist. Ein Gerät kann nur in einer Gruppe sein.
        </p>
      </div>

      {groups === null ? (
        <div className="font-mono text-xs text-cyan-400 animate-pulse" data-testid="groups-loading">Lade Gruppen…</div>
      ) : groups.length === 0 ? (
        <EmptyState text="Noch keine Gruppen — lege die erste Gruppe an" testid="groups-empty" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {groups.map((g, i) => (
            <div key={g.id} className="panel p-5" data-testid={`group-card-${i}`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-2.5 min-w-0">
                  <Layers size={16} className="text-cyan-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-display font-bold text-slate-100 truncate" data-testid={`group-name-${i}`}>{g.name}</div>
                    <div className="font-mono text-[10px] text-slate-400 uppercase tracking-widest" data-testid={`group-count-${i}`}>
                      {g.member_count} Gerät{g.member_count === 1 ? "" : "e"}
                    </div>
                  </div>
                </div>
                {deleteArmed === g.id ? (
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => setDeleteArmed(null)} className="btn-ghost !py-1 !px-2 text-[11px]" data-testid={`group-delete-cancel-${i}`}>
                      Abbrechen
                    </button>
                    <button
                      onClick={() => deleteGroup(g)}
                      className="inline-flex items-center rounded-sm bg-red-500 px-2 py-1 text-[11px] font-semibold text-white hover:bg-red-400"
                      data-testid={`group-delete-confirm-${i}`}
                    >
                      Löschen
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteArmed(g.id)}
                    className="p-1.5 text-slate-400 hover:text-red-400 transition-colors shrink-0"
                    title="Gruppe löschen"
                    data-testid={`group-delete-${i}`}
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="mono-label mb-1.5">Wiedergabeliste für alle Geräte</div>
              <select
                value={g.playlist_id || ""}
                onChange={(e) => setPlaylist(g, e.target.value)}
                className="input-dark"
                data-testid={`group-playlist-select-${i}`}
              >
                <option value="">— Keine —</option>
                {playlists.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} (v{p.version})
                  </option>
                ))}
              </select>

              <button onClick={() => openDevices(g)} className="btn-ghost w-full mt-4 text-xs" data-testid={`group-devices-open-${i}`}>
                <MonitorSmartphone size={13} /> Geräte verwalten ({g.member_count})
              </button>

              <div className="flex gap-1.5 mt-2" data-testid={`group-commands-${i}`}>
                {GROUP_COMMANDS.map((c) => (
                  <button
                    key={c.type}
                    onClick={() => groupCommand(g, c.type)}
                    disabled={!g.member_count}
                    className="btn-ghost flex-1 !py-1.5 text-[11px] disabled:opacity-40"
                    title={`${c.label} an alle ${g.member_count} Geräte der Gruppe senden`}
                    data-testid={`group-command-${c.type.toLowerCase()}-${i}`}
                  >
                    <c.icon size={11} /> {c.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Neue Gruppe */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="group-create-dialog">
          <DialogTitle>Neue Gruppe</DialogTitle>
          <div className="space-y-4 mt-2">
            <div>
              <div className="mono-label mb-1.5">Name</div>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="z. B. Schaufenster Filiale Mitte"
                className="input-dark"
                data-testid="group-name-input"
              />
            </div>
            <div>
              <div className="mono-label mb-1.5">Wiedergabeliste (optional)</div>
              <select value={newPlaylist} onChange={(e) => setNewPlaylist(e.target.value)} className="input-dark" data-testid="group-playlist-select-new">
                <option value="">— Später zuweisen —</option>
                {playlists.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} (v{p.version})</option>
                ))}
              </select>
            </div>
            <button onClick={createGroup} disabled={!newName.trim() || busy} className="btn-primary w-full" data-testid="group-create-submit">
              {busy ? "Lege an…" : "Gruppe anlegen"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Geräte-Zuordnung */}
      <Dialog open={!!devicesFor} onOpenChange={(o) => !o && setDevicesFor(null)}>
        <DialogContent data-testid="group-devices-dialog">
          <DialogTitle>Geräte in „{devicesFor?.name}"</DialogTitle>
          <div className="mt-2 max-h-72 overflow-y-auto space-y-1" data-testid="group-devices-list">
            {pairedDevices.length === 0 && (
              <div className="font-mono text-xs text-slate-400 py-4">Keine gekoppelten Geräte vorhanden</div>
            )}
            {pairedDevices.map((d) => {
              const inOther = d.group_id && d.group_id !== devicesFor?.id;
              const checked = selected.includes(d.id);
              return (
                <label
                  key={d.id}
                  className={`flex items-center gap-3 px-3 py-2 rounded-sm border cursor-pointer transition-colors ${
                    checked ? "border-cyan-500/50 bg-cyan-500/5" : "border-border hover:bg-slate-800/40"
                  }`}
                  data-testid={`group-device-row-${d.id.slice(0, 8)}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      setSelected((s) => (e.target.checked ? [...s, d.id] : s.filter((x) => x !== d.id)))
                    }
                    className="accent-cyan-400"
                    data-testid={`group-device-check-${d.id.slice(0, 8)}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-200 truncate">{d.display_name || d.label || d.id}</div>
                    <div className="font-mono text-[10px] text-slate-400">
                      {d.location_name || "ohne Standort"}
                      {inOther ? ` · wechselt aus „${groupNames[d.group_id] || "anderer Gruppe"}"` : ""}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
          <button onClick={saveDevices} disabled={busy} className="btn-primary w-full mt-4" data-testid="group-devices-save">
            {busy ? "Speichere…" : `Übernehmen (${selected.length} ausgewählt)`}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
